/**
 * roadmapSync.ts — Konsolidierte Roadmap-Daten aus Google Drive
 * 
 * Diese Datei enthält alle Roadmap-Einträge aus den 3 Google Drive Dateien:
 * - ROADMAP_TODO_ANDREAS.md (10.04.2026) — Persönliche TODOs + System-Roadmap + Daten-Konsolidierung
 * - ROADMAP_GUTACHTEN_SOFTWARE.md (26.03.2026) — Software-Phasen 1-5
 * - ROADMAP_TODO_ANDREAS_2.md (08.04.2026) — Ältere Version (Duplikate entfernt)
 * 
 * Wird beim ersten Laden der Kommandozentrale geprüft und synchronisiert.
 * Jeder Eintrag hat eine eindeutige sync_id, damit keine Duplikate entstehen.
 */

export interface RoadmapSyncItem {
  sync_id: string;           // Eindeutige ID für Duplikat-Erkennung
  titel: string;
  inhalt: string;
  typ: string;               // feature | bugfix | verbesserung | idee | todo | api_todo | mcp_todo
  kategorie: string;         // app | agenten | buchhaltung | marketing | technik | immobilien | allgemein | personal | integration
  status: string;            // idee | geplant | in_arbeit | review | fertig
  prioritaet: number;        // 1-10
  quelle: string;            // Welche Datei auf Google Drive
  abhaengigkeit?: string;    // Was muss vorher passieren?
  anleitung?: string;        // Schritt-für-Schritt Anleitung (für persönliche TODOs)
}

export const ROADMAP_SYNC_ITEMS: RoadmapSyncItem[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // 1. PERSÖNLICHE TODOS (Andreas) — aus ROADMAP_TODO_ANDREAS.md
  // ═══════════════════════════════════════════════════════════════════════════
  {
    sync_id: 'personal-google-cloud-verknuepfung',
    titel: 'Google Cloud Projekt verknüpfen',
    inhalt: 'Das Google Cloud Projekt "Kfz Gutachter Koop" muss mit der Google Workspace Organisation (gutachter-koop.de) verknüpft werden. Dies ist notwendig, damit OAuth-Apps als "Intern" eingerichtet werden können.\n\nAnleitung:\n1. Admin Console öffnen (admin.google.com)\n2. Zu "Cloud-Projekte" navigieren\n3. "Projekt hinzufügen" wählen und verknüpfen.',
    typ: 'todo',
    kategorie: 'technik',
    status: 'geplant',
    prioritaet: 9,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
    anleitung: '1. admin.google.com → Cloud-Projekte → Projekt hinzufügen',
  },
  {
    sync_id: 'personal-oauth-client-id',
    titel: 'Google OAuth Client ID erstellen',
    inhalt: 'Für die Kalender-Integration wird eine OAuth Client ID benötigt.\n\nAnleitung:\n1. Google Cloud Console öffnen (console.cloud.google.com)\n2. Projekt "Kfz Gutachter Koop" auswählen\n3. Zu "APIs & Dienste" → "Anmeldedaten" navigieren\n4. "OAuth 2.0 Client ID erstellen" klicken\n5. Typ: Webanwendung\n6. Name: Gutachter Koop App\n7. Redirect URI: https://app.gutachter-koop.de/auth/google/callback\n8. Client ID und Secret an Manus schicken.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 9,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
    abhaengigkeit: 'Google Cloud Projekt verknüpfen',
    anleitung: '1. console.cloud.google.com → APIs & Dienste → Anmeldedaten → OAuth 2.0 Client ID erstellen',
  },
  {
    sync_id: 'personal-google-maps-api-key',
    titel: 'Google Maps API Key erstellen',
    inhalt: 'Die Karte auf gutachter-koop.de zeigt aktuell den Fehler "Google Maps API Key fehlt".\n\nAnleitung:\n1. Google Cloud Console öffnen\n2. Projekt "Kfz Gutachter Koop" auswählen\n3. APIs & Dienste → Anmeldedaten → API-Key erstellen\n4. Maps JavaScript API aktivieren\n5. Key in Vercel als NEXT_PUBLIC_GOOGLE_MAPS_API_KEY eintragen.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 7,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
    anleitung: '1. console.cloud.google.com → API-Key erstellen → Maps JavaScript API aktivieren → Vercel Env Var setzen',
  },
  {
    sync_id: 'personal-dropbox-api',
    titel: 'Dropbox API App erstellen',
    inhalt: 'Am 10.04.2026 erledigt: Die App "AK-Manus" wurde erfolgreich mit dem Team Koop Dropbox Account verknüpft.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'fertig',
    prioritaet: 5,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. DATEN-KONSOLIDIERUNG (Dropbox & Google Drive) — 6 Phasen
  // ═══════════════════════════════════════════════════════════════════════════
  {
    sync_id: 'daten-phase1-bestandsaufnahme',
    titel: 'Daten-Konsolidierung Phase 1: Bestandsaufnahme & API-Anbindung',
    inhalt: 'Dropbox-Struktur analysiert (182 Team-Namespaces, 95 Akten im Root-Ordner "1. Gutachten - Fotos - Dokumente"). OAuth2-Token generiert.',
    typ: 'feature',
    kategorie: 'technik',
    status: 'fertig',
    prioritaet: 8,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'daten-phase2-dropbox-aufraeumen',
    titel: 'Daten-Konsolidierung Phase 2: Dropbox Aufräumen (Analyse)',
    inhalt: 'Jede einzelne Datei und jeden Ordner in der Dropbox analysieren. Privat und Betriebe trennen. Duplikate identifizieren.',
    typ: 'feature',
    kategorie: 'technik',
    status: 'geplant',
    prioritaet: 8,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'daten-phase3-drive-abgleich',
    titel: 'Daten-Konsolidierung Phase 3: Google Drive Abgleich',
    inhalt: 'Google Drive nach Duplikaten durchsuchen (insbesondere alte Umzugs-Versuche) und mit der aufgeräumten Dropbox-Struktur abgleichen.',
    typ: 'feature',
    kategorie: 'technik',
    status: 'geplant',
    prioritaet: 7,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'daten-phase4-supabase-struktur',
    titel: 'Daten-Konsolidierung Phase 4: Struktur in Supabase anlegen',
    inhalt: 'Die aufgeräumte Struktur (Firmen, Immobilien, Gutachten, Buchhaltung) als saubere Ordnerstruktur in der Supabase-Datenbank (dokument_ordner) abbilden.',
    typ: 'feature',
    kategorie: 'technik',
    status: 'geplant',
    prioritaet: 7,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'daten-phase5-migration',
    titel: 'Daten-Konsolidierung Phase 5: Daten-Migration & Sync',
    inhalt: 'Physischer Umzug der Dateien von Dropbox/Drive in die Supabase Storage Buckets. In der Dropbox werden die umgezogenen Ordner als "Synchronisiert" markiert.',
    typ: 'feature',
    kategorie: 'technik',
    status: 'idee',
    prioritaet: 6,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'daten-phase6-winvalue-aktenzeichen',
    titel: 'Daten-Konsolidierung Phase 6: WinValue-Aktenzeichen',
    inhalt: 'Die bestehenden MW-Aktenzeichen in der App werden mit den echten WinValue-Aktenzeichen (z.B. AK2601) aus der Dropbox verknüpft.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 6,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. SYSTEM-ROADMAP (Manus) — aus ROADMAP_TODO_ANDREAS.md
  // ═══════════════════════════════════════════════════════════════════════════
  {
    sync_id: 'system-winvalue-api-haken',
    titel: 'WinValue API-Haken setzen (alle 6.661 Dossiers)',
    inhalt: 'Playwright-Skript ist fertig entwickelt und getestet (10/10 Dry-Run erfolgreich). Muss für alle 6.661 Dossiers ausgeführt werden, damit die Public API Zugriff auf alle Akten hat. Geschätzte Dauer: ca. 6 Stunden.\n\nAnleitung: python3 winvalue_api_aktivieren.py --skip-collect ausführen. Danach können Versicherungsdaten für die 60 fehlenden MW-Akten automatisch abgeglichen werden.',
    typ: 'feature',
    kategorie: 'technik',
    status: 'geplant',
    prioritaet: 9,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'system-twilio-integration',
    titel: 'Twilio Integration (SMS/WhatsApp)',
    inhalt: 'Einrichtung von SMS/WhatsApp Kundenbenachrichtigungen speziell für das Werkstatt-Modul.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 6,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
    abhaengigkeit: 'Twilio Account + API Key benötigt',
  },
  {
    sync_id: 'system-google-calendar-sync',
    titel: 'Google Calendar Sync (bidirektional)',
    inhalt: 'Bidirektionale Synchronisation des Kalenders. Wartet auf die OAuth Client ID von Andreas.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 8,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
    abhaengigkeit: 'Google OAuth Client ID erstellen',
  },
  {
    sync_id: 'system-kiosk-persistieren',
    titel: 'Kiosk-Aufträge persistieren',
    inhalt: 'Aufträge aus dem Kiosk-Modus sollen direkt als Akte in der Supabase-Datenbank gespeichert werden.',
    typ: 'feature',
    kategorie: 'app',
    status: 'geplant',
    prioritaet: 7,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'system-werkstatt-cockpit-aufgaben',
    titel: 'Werkstatt-Cockpit Aufgaben persistieren',
    inhalt: 'Aufgaben im Werkstatt-Cockpit sollen in Supabase persistiert werden, damit sie teamweit sichtbar sind.',
    typ: 'feature',
    kategorie: 'app',
    status: 'geplant',
    prioritaet: 7,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'system-mietgo-rentsoft',
    titel: 'MietGo/Rentsoft Anbindung',
    inhalt: 'Integration der Rentsoft-Schnittstelle für das Mietwagen-Modul (LCS GmbH).',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 6,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
    abhaengigkeit: 'Rentsoft API-Zugangsdaten benötigt',
  },
  {
    sync_id: 'system-lenneglas',
    titel: 'Lenneglas Anbindung',
    inhalt: 'Integration spezifischer Funktionen für das Autoglas-Modul.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 4,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'system-airbnb-channel-manager',
    titel: 'Airbnb API / Channel Manager (Smoobu/Hostaway)',
    inhalt: 'Anbindung von Smoobu oder Hostaway für die Verwaltung der Immobilie Glatzer Straße 8.',
    typ: 'api_todo',
    kategorie: 'immobilien',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
    abhaengigkeit: 'Smoobu oder Hostaway Account benötigt',
  },
  {
    sync_id: 'system-fahrzeugmanager-agent',
    titel: 'Fahrzeugmanager-Agent (10. Agent)',
    inhalt: 'Einrichtung des 10. Agenten, der speziell als Fahrzeugmanager fungiert.',
    typ: 'feature',
    kategorie: 'agenten',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'system-dokumentenbereich-storage',
    titel: 'Dokumentenbereich: Storage & Ordnerstruktur',
    inhalt: 'Am 10.04.2026 umgesetzt: Storage Buckets (akten-dateien, dokumente) eingerichtet, RLS-Policies für anon+authenticated erstellt, 15 Root-Ordner + 611 Aktenordner (je 6 Unterordner) angelegt = 4.553 Ordner gesamt.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 8,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. SEO Hub & Spoke — aus ROADMAP_TODO_ANDREAS_2.md
  // ═══════════════════════════════════════════════════════════════════════════
  {
    sync_id: 'seo-hub-spoke-wuppertal',
    titel: 'SEO Hub & Spoke Pilot: Wuppertal',
    inhalt: 'Prüftermin: 29.04.2026 — Am 08.04.2026 wurden 6 Unterseiten hinter der Wuppertal-Städteseite gebaut (Hub & Spoke Modell). Jede Seite hat einzigartigen, stadtspezifischen Content. Nach 3 Wochen: Rankings in Google Search Console messen. Bei positiven Ergebnissen: Rollout auf Dortmund, Köln, Essen, Bochum.',
    typ: 'feature',
    kategorie: 'marketing',
    status: 'in_arbeit',
    prioritaet: 7,
    quelle: 'ROADMAP_TODO_ANDREAS_2.md',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 5. GESCHÄFTLICHE TODOS — aus ROADMAP_TODO_ANDREAS.md
  // ═══════════════════════════════════════════════════════════════════════════
  {
    sync_id: 'geschaeft-markenrecht-koop',
    titel: 'Markenrecht prüfen: koop-fahrzeugcheck.de',
    inhalt: 'Maxim Koop (Osterholz-Scharmbeck) betreibt koop-fahrzeugcheck.de als Kfz-Sachverständiger. Nutzt den gleichen Nachnamen. Rechtlich: Er darf seinen Nachnamen nutzen (§12 BGB), aber die eingetragene Marke schützt die Kombination "KOOP KFZ-GUTACHTEN". Bei Bedarf Anwalt Steinhauer & Günther (Menden) konsultieren.',
    typ: 'todo',
    kategorie: 'allgemein',
    status: 'geplant',
    prioritaet: 5,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'geschaeft-stellungnahme-elmas',
    titel: 'Stellungnahme "Elmas v10" senden',
    inhalt: 'Die Stellungnahme "Elmas v10" muss an die Direct Line Insurance gesendet werden.',
    typ: 'todo',
    kategorie: 'allgemein',
    status: 'geplant',
    prioritaet: 7,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },
  {
    sync_id: 'geschaeft-workspace-verknuepfung',
    titel: 'Workspace-Verknüpfung prüfen',
    inhalt: 'Sicherstellen, dass die Verknüpfung der Google Workspace Organisation erfolgreich war.',
    typ: 'todo',
    kategorie: 'technik',
    status: 'geplant',
    prioritaet: 6,
    quelle: 'ROADMAP_TODO_ANDREAS.md',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 6. GUTACHTEN-SOFTWARE ROADMAP — aus ROADMAP_GUTACHTEN_SOFTWARE.md
  // ═══════════════════════════════════════════════════════════════════════════

  // Phase 1 — Akten UX verbessern
  {
    sync_id: 'software-p1-breadcrumb',
    titel: 'Akten: Breadcrumb-Navigation',
    inhalt: 'Anwender sieht immer: Akten > Akte #123 > Fotos. Kein Verloren-gehen mehr.',
    typ: 'feature',
    kategorie: 'app',
    status: 'geplant',
    prioritaet: 8,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p1-fortschrittsanzeige',
    titel: 'Akten: Fortschrittsanzeige pro Akte',
    inhalt: 'Visuelle Schritte-Leiste (Stammdaten ✓, Fotos ✓, Kalkulation ○, Gutachten ○). Zeigt an, was noch fehlt.',
    typ: 'feature',
    kategorie: 'app',
    status: 'geplant',
    prioritaet: 8,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p1-schnellzugriff',
    titel: 'Akten: Schnellzugriff-Aktionen',
    inhalt: 'Floating-Button oder Sidebar mit den 3 häufigsten Aktionen je nach Akten-Status (z.B. "Foto hochladen", "Termin anlegen", "Gutachten erstellen").',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 6,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p1-status-system',
    titel: 'Akten: Status-System',
    inhalt: 'Klare Status-Labels: Neu → In Bearbeitung → Gutachten erstellt → Abgeschlossen. Mit Farb-Kodierung.',
    typ: 'feature',
    kategorie: 'app',
    status: 'geplant',
    prioritaet: 8,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p1-onboarding-wizard',
    titel: 'Akten: Onboarding-Wizard',
    inhalt: 'Kontextsensitiver Hilfe-Wizard, der neue Mitarbeiter durch die Akte führt. Fragezeichen-Button auf jeder Seite.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },

  // Phase 2 — WinValue-Integration
  {
    sync_id: 'software-p2-winvalue-api',
    titel: 'WinValue API-Anbindung (bidirektional)',
    inhalt: 'Akten parallel in eigenem System UND WinValue führen. Änderungen werden via WinValue-API synchronisiert. Kein doppelter Dateneintrag.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 9,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'WinValue API-Haken müssen gesetzt sein',
  },
  {
    sync_id: 'software-p2-bildverwaltung',
    titel: 'Bildverwaltungssystem (Supabase Storage)',
    inhalt: 'Supabase Storage für Fahrzeugfotos. Werkstatt-Upload per QR-Code. Inbox-Freigabe durch Sachverständigen. Konzept bereits fertig.',
    typ: 'feature',
    kategorie: 'app',
    status: 'geplant',
    prioritaet: 8,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p2-bilder-freigabe',
    titel: 'Bilder-Freigabe für Dritte',
    inhalt: 'Sicherer Download-Link mit Ablaufdatum für Gerichtsgutachter/Anwalt. Automatische E-Mail von info@gutachter-koop.de. Benachrichtigung bei Download.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 6,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p2-gutachten-pdf',
    titel: 'Gutachten-PDF-Erstellung (eigenes Layout)',
    inhalt: 'Gutachten direkt aus dem Dashboard als professionelles PDF generieren. Eigenes Layout mit Koop-Branding.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 7,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p2-bild-upload',
    titel: 'Bild-Upload in Akten',
    inhalt: 'Mitarbeiter laden Fotos direkt in die Akte hoch. Gespeichert in Supabase Storage. Später mit WinValue verknüpfbar.',
    typ: 'feature',
    kategorie: 'app',
    status: 'geplant',
    prioritaet: 8,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },

  // Phase 3 — Eigene Gutachten-Software
  {
    sync_id: 'software-p3-vollstaendige-gutachten',
    titel: 'Vollständige Gutachten-Erstellung (WinValue-Ablösung)',
    inhalt: 'Alle WinValue-Funktionen im eigenen System: Stammdaten, Fotos, Kalkulation, Gutachten-PDF, Kommunikation, Dokumente.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 7,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p3-ki-kalkulation',
    titel: 'KI-gestützte Kalkulation',
    inhalt: 'Automatische Schadensschätzung auf Basis von Fotos (KI-Analyse). Vorschlag für Reparaturkosten und Wiederbeschaffungswert.',
    typ: 'feature',
    kategorie: 'agenten',
    status: 'idee',
    prioritaet: 6,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p3-digitale-unterschrift',
    titel: 'Digitale Unterschrift (DocuSign)',
    inhalt: 'Gutachten direkt im Browser unterschreiben. Rechtssicher via DocuSign-Integration oder eigene Lösung.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'DocuSign Account benötigt',
  },

  // Phase 3.2 — Rechnungs-Module
  {
    sync_id: 'software-p3-autoglas-rechnungen',
    titel: 'Autoglas-Rechnungen (Lenneglas)',
    inhalt: 'Rechnungserstellung für Glasschäden, Versicherungsabrechnung, Direktabrechnung.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p3-mietwagen-rechnungen',
    titel: 'Mietwagen-Rechnungen (LCS)',
    inhalt: 'Mietwagenabrechnung, Tagespreise, Versicherungsabrechnung.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p3-demontage-rechnungen',
    titel: 'Demontage-Rechnungen (LCS GmbH)',
    inhalt: 'Eigenes Modul (Easywerkstatt hat keine API). Leistungspositionen, Stundenabrechnung, PDF-Export.',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p3-rechnungen-aus-akte',
    titel: 'Rechnungen direkt aus Akte generieren',
    inhalt: 'Rechnung direkt aus einer Akte generieren. Kostenpflichtige Leistungen abrechnen (z.B. Bildbereitstellung für Gericht).',
    typ: 'feature',
    kategorie: 'app',
    status: 'idee',
    prioritaet: 6,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },

  // Phase 4 — Koop AI
  {
    sync_id: 'software-p4-foto-sync-ki',
    titel: 'Koop AI: Foto-Sync & KI-Kategorisierung',
    inhalt: 'Täglicher Abruf neuer Smartphone-Fotos, Analyse durch KI und automatische Ablage (Belege in Drive/Supabase, Löschen vom Handy). Benötigt: Google Photos API, OpenAI GPT-4 Vision.',
    typ: 'api_todo',
    kategorie: 'agenten',
    status: 'idee',
    prioritaet: 6,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'Google Photos API + OpenAI Key',
  },
  {
    sync_id: 'software-p4-taegliches-briefing',
    titel: 'Koop AI: Tägliches Briefing',
    inhalt: 'Zusammenfassung von E-Mails, Terminen, Aufgaben und erkannten Belegen. Automatischer Versand (05:30, 11:00, 18:00 Uhr).',
    typ: 'feature',
    kategorie: 'agenten',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p4-sprachassistent',
    titel: 'Koop AI: Interaktiver Sprachassistent',
    inhalt: 'Sprachsteuerung für den Zugriff auf die gesamte Firmenstruktur und Ausführung von Aktionen. Benötigt: OpenAI Whisper & TTS.',
    typ: 'api_todo',
    kategorie: 'agenten',
    status: 'idee',
    prioritaet: 4,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'OpenAI API Key',
  },

  // Phase 5 — Unfallgiganten Content Creator
  {
    sync_id: 'software-p5-ki-artikel-generator',
    titel: 'Unfallgiganten: KI-Artikel-Generator',
    inhalt: 'OpenAI-Integration zur Generierung von SEO-optimierten Ratgeber-Artikeln aus echten Falldaten.',
    typ: 'api_todo',
    kategorie: 'marketing',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'OpenAI API Key',
  },
  {
    sync_id: 'software-p5-content-pipeline',
    titel: 'Unfallgiganten: Content-Pipeline',
    inhalt: 'Kanban/Kalender-UI im Dashboard zur Planung, Überprüfung und Freigabe von Inhalten.',
    typ: 'feature',
    kategorie: 'marketing',
    status: 'idee',
    prioritaet: 5,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'software-p5-wordpress',
    titel: 'Unfallgiganten: WordPress-Anbindung',
    inhalt: 'Automatisches Veröffentlichen von freigegebenen Artikeln auf unfallgiganten.de via REST API.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'idee',
    prioritaet: 4,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'WordPress REST API Credentials',
  },
  {
    sync_id: 'software-p5-social-media',
    titel: 'Unfallgiganten: Social Media Automatisierung',
    inhalt: 'Generierung und zeitgesteuertes Posten von Social-Media-Beiträgen via Meta & LinkedIn API.',
    typ: 'api_todo',
    kategorie: 'marketing',
    status: 'idee',
    prioritaet: 4,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'Meta Business API + LinkedIn API Credentials',
  },
  {
    sync_id: 'software-p5-seo-analytics',
    titel: 'Unfallgiganten: SEO & Analytics',
    inhalt: 'Tracking von Keyword-Rankings und Performance-Daten über die Google Search Console API.',
    typ: 'api_todo',
    kategorie: 'marketing',
    status: 'idee',
    prioritaet: 3,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
    abhaengigkeit: 'Google Search Console API Zugang',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 7. MCP-CONNECTOREN & API-VERBINDUNGEN (TODOs für Andreas)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    sync_id: 'mcp-webflow-connector',
    titel: 'MCP: Webflow Connector',
    inhalt: 'Webflow MCP Server ist konfiguriert und aktiv. Kann für Website-Content-Automatisierung, CMS-Management und Design-Workflow genutzt werden. Status: VERBUNDEN.',
    typ: 'mcp_todo',
    kategorie: 'integration',
    status: 'fertig',
    prioritaet: 5,
    quelle: 'MCP-System',
  },
  {
    sync_id: 'mcp-stripe-connector',
    titel: 'MCP: Stripe Connector',
    inhalt: 'Stripe MCP Server ist konfiguriert. Kann für E-Commerce-Payment, Subscription-Billing und Kundenverwaltung genutzt werden. Status: VERBUNDEN.',
    typ: 'mcp_todo',
    kategorie: 'integration',
    status: 'fertig',
    prioritaet: 5,
    quelle: 'MCP-System',
  },
  {
    sync_id: 'mcp-gmail-connector',
    titel: 'MCP: Gmail Connector',
    inhalt: 'Gmail MCP Server ist konfiguriert und aktiv. Kann E-Mails lesen, senden und verwalten. Status: VERBUNDEN.',
    typ: 'mcp_todo',
    kategorie: 'integration',
    status: 'fertig',
    prioritaet: 5,
    quelle: 'MCP-System',
  },
  {
    sync_id: 'mcp-instagram-connector',
    titel: 'MCP: Instagram Connector',
    inhalt: 'Instagram MCP Server ist konfiguriert. Kann Content publizieren, Account-Info abrufen und Post-Insights analysieren. Status: VERBUNDEN.',
    typ: 'mcp_todo',
    kategorie: 'integration',
    status: 'fertig',
    prioritaet: 5,
    quelle: 'MCP-System',
  },
  {
    sync_id: 'api-openai-key',
    titel: 'API: OpenAI API Key einrichten',
    inhalt: 'OpenAI API Key wird benötigt für: KI-Sprachassistent, KI-Kalkulation, Foto-Kategorisierung, Artikel-Generator. In der App unter Einstellungen → API-Schlüssel eintragen.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 9,
    quelle: 'System-Analyse',
    anleitung: '1. platform.openai.com → API Keys → Create new secret key\n2. In der App: Einstellungen → API-Schlüssel → OpenAI Key eintragen',
  },
  {
    sync_id: 'api-winvalue-credentials',
    titel: 'API: WinValue API Zugangsdaten',
    inhalt: 'WinValue UltraExpert API (Kundennr: 925621). Playwright-Skript für API-Haken ist fertig. Public API muss für alle 6.661 Dossiers aktiviert werden.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'in_arbeit',
    prioritaet: 9,
    quelle: 'System-Analyse',
  },
  {
    sync_id: 'api-superchat',
    titel: 'API: Superchat Anbindung',
    inhalt: 'Superchat API für Kundenkommunikation. Muss mit dem bestehenden Superchat-Account verbunden werden.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 6,
    quelle: 'System-Analyse',
    abhaengigkeit: 'Superchat API Key benötigt',
  },
  {
    sync_id: 'api-finapi',
    titel: 'API: finAPI Bankanbindung',
    inhalt: 'finAPI für automatischen Kontoabruf und Zahlungsabgleich. Wird für die Buchhaltungs-Automatisierung benötigt.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 7,
    quelle: 'System-Analyse',
    abhaengigkeit: 'finAPI Account + API Key benötigt',
  },
  {
    sync_id: 'api-rentsoft',
    titel: 'API: Rentsoft Mietwagen-Schnittstelle',
    inhalt: 'Rentsoft API für das Mietwagen-Modul (LCS GmbH / MietGo). Fahrzeugverfügbarkeit, Buchungen, Preise.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 6,
    quelle: 'System-Analyse',
    abhaengigkeit: 'Rentsoft API-Zugangsdaten benötigt',
  },
  {
    sync_id: 'api-twilio',
    titel: 'API: Twilio SMS/WhatsApp',
    inhalt: 'Twilio für SMS und WhatsApp Kundenbenachrichtigungen. Besonders für das Werkstatt-Modul.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'geplant',
    prioritaet: 6,
    quelle: 'System-Analyse',
    abhaengigkeit: 'Twilio Account + API Key benötigt',
  },
  {
    sync_id: 'api-google-photos',
    titel: 'API: Google Photos (Foto-Sync)',
    inhalt: 'Google Photos API für automatischen Foto-Abruf vom Smartphone. Wird für die KI-Kategorisierung benötigt.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'idee',
    prioritaet: 5,
    quelle: 'System-Analyse',
    abhaengigkeit: 'Google Cloud Projekt + OAuth',
  },
  {
    sync_id: 'api-docusign',
    titel: 'API: DocuSign (Digitale Unterschrift)',
    inhalt: 'DocuSign API für rechtssichere digitale Unterschriften auf Gutachten.',
    typ: 'api_todo',
    kategorie: 'integration',
    status: 'idee',
    prioritaet: 4,
    quelle: 'System-Analyse',
    abhaengigkeit: 'DocuSign Account benötigt',
  },
  {
    sync_id: 'api-meta-business',
    titel: 'API: Meta Business (Facebook/Instagram)',
    inhalt: 'Meta Business API für automatisiertes Social Media Posting. Wird für den Unfallgiganten Content Creator benötigt.',
    typ: 'api_todo',
    kategorie: 'marketing',
    status: 'idee',
    prioritaet: 4,
    quelle: 'System-Analyse',
    abhaengigkeit: 'Meta Business Account + App erstellen',
  },
  {
    sync_id: 'api-google-search-console',
    titel: 'API: Google Search Console',
    inhalt: 'Google Search Console API für SEO-Tracking und Keyword-Rankings von unfallgiganten.de und gutachter-koop.de.',
    typ: 'api_todo',
    kategorie: 'marketing',
    status: 'idee',
    prioritaet: 3,
    quelle: 'System-Analyse',
    abhaengigkeit: 'Google Cloud Projekt + Search Console Zugang',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 8. LIVE / FERTIGE FEATURES — aus ROADMAP_GUTACHTEN_SOFTWARE.md
  // ═══════════════════════════════════════════════════════════════════════════
  {
    sync_id: 'live-dashboard',
    titel: 'Dashboard mit Echtzeit-Übersicht',
    inhalt: 'Kalender, Team-Status, Aufgaben, Statistiken — alles live aus Supabase. Live seit 24.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-google-calendar',
    titel: 'Google Calendar Integration (Frontend)',
    inhalt: 'Verbindung, Wochenansicht, Uhrzeit-Linie, Drag & Drop, Terminblöcke mit Höhe. Live seit 25.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-kalender-raster',
    titel: 'Kalender: 30-Minuten-Raster',
    inhalt: 'Proportionale Terminblöcke (80px/h), Collision Detection, Doppelklick-Bearbeitung. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-werkstatt-kiosk',
    titel: 'Werkstatt-Kiosk Dashboard',
    inhalt: 'Fallübersicht, Kalender, Aufgaben, Echtzeit-Uhr — alle Demo-Daten entfernt. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-team-verwaltung',
    titel: 'Team-Verwaltung',
    inhalt: 'Echte Accounts, Berechtigungs-Matrix (Rollen × Betriebe × Module), Online-Status. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-rechnungssystem',
    titel: 'Rechnungssystem',
    inhalt: 'PDF-Generierung, Nummernkreise pro Betrieb, DATEV-Export. Live seit 25.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-dokumentensystem',
    titel: 'Dokumentensystem',
    inhalt: 'Ordnerstruktur, Upload, Vorlagen. Live seit 25.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-forderungsmanagement',
    titel: 'Forderungsmanagement Mietwagen',
    inhalt: '5 Module, Schadensminderungspflicht-Protokoll. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-fahrzeugverwaltung',
    titel: 'Fahrzeugverwaltung',
    inhalt: 'BMW iX1 angelegt, Finanzierungsdaten, Übersicht. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-aufgabenmodul',
    titel: 'Aufgabenmodul',
    inhalt: 'Wiedervorlage, Glocken-Erinnerung, Kategorien. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-finanzen-menue',
    titel: 'Finanzen-Menü',
    inhalt: 'Untermenü mit Liquiditätsplanung, Bankkonten, Steuern (Platzhalter). Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-sicherheit',
    titel: 'Sicherheit (RLS-Policies)',
    inhalt: '24 RLS-Policies, alle Tabellen abgesichert, Performance-Indizes. Live seit 25.03.2026.',
    typ: 'feature',
    kategorie: 'technik',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-ki-sprachassistent',
    titel: 'KI-Sprachassistent (Frontend)',
    inhalt: 'Frontend fertig, Edge Function deployed — wartet auf OpenAI Key. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'agenten',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-setup-assistent',
    titel: 'Setup-Assistent',
    inhalt: 'Interaktive Checkliste für manuelle Setup-Schritte (6 Punkte). Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
  {
    sync_id: 'live-api-schluessel',
    titel: 'API-Schlüssel & Integrationen (UI)',
    inhalt: 'Bereich für OpenAI, WinValue, Rentsoft, Finapi, Google Maps mit Status. Live seit 26.03.2026.',
    typ: 'feature',
    kategorie: 'app',
    status: 'fertig',
    prioritaet: 10,
    quelle: 'ROADMAP_GUTACHTEN_SOFTWARE.md',
  },
];

// Statistiken
export const ROADMAP_STATS = {
  gesamt: ROADMAP_SYNC_ITEMS.length,
  fertig: ROADMAP_SYNC_ITEMS.filter(i => i.status === 'fertig').length,
  inArbeit: ROADMAP_SYNC_ITEMS.filter(i => i.status === 'in_arbeit').length,
  geplant: ROADMAP_SYNC_ITEMS.filter(i => i.status === 'geplant').length,
  ideen: ROADMAP_SYNC_ITEMS.filter(i => i.status === 'idee').length,
  apiTodos: ROADMAP_SYNC_ITEMS.filter(i => i.typ === 'api_todo').length,
  mcpTodos: ROADMAP_SYNC_ITEMS.filter(i => i.typ === 'mcp_todo').length,
  personalTodos: ROADMAP_SYNC_ITEMS.filter(i => i.typ === 'todo').length,
};
